// Copyright (c) 2026 Signal from Noise, Inc. All rights reserved.

import { createSolidStreamingMarkdown, SolidStreamingMarkdown } from "@storytell/markdown";
import type { StreamingBlock } from "@storytell/streaming";
import { getBlockContent } from "@storytell/streaming";
import { StTooltip, StTooltipTrigger } from "@storytell/ui/tooltip";
import { TbCheck, TbDownload, TbLoader2, TbPlus, TbRobot, TbUser, TbWand } from "solid-icons/tb";
import { createEffect, createMemo, createSignal, on, onCleanup, Show } from "solid-js";
import { defaultMarkdownOptions } from "../block/markdownOptions";
import { safeParseBlockJson } from "../message/parseBlockContent";
import { useChatRuntime } from "../runtime/ChatRuntime";
import { ScrollableFadeContainer } from "../ui";
import { sanitizeForFilename } from "../util/download";
import { getScopeBadgeClasses } from "./activityUIHelpers";

interface CrafterData {
  type: "crafter";
  version: number;
  name: string;
  displayName: string;
  description: string;
  content: string;
  context?: "agent";
  agent?: string;
  category?: "skill" | "persona";
}

interface CrafterRendererProps {
  block: StreamingBlock;
}

export function CrafterRenderer(props: CrafterRendererProps) {
  const rt = useChatRuntime();
  const [showScopeMenu, setShowScopeMenu] = createSignal(false);
  const [saveState, setSaveState] = createSignal<"idle" | "saving" | "error">("idle");
  const [savedScopes, setSavedScopes] = createSignal<Set<string>>(new Set());
  const [selectedCategory, setSelectedCategory] = createSignal<"skill" | "persona">("skill");

  const crafterData = createMemo<CrafterData | null>(() => {
    if (props.block.finalContent?.type === "text" && props.block.finalContent.text) {
      const parsed = safeParseBlockJson(props.block.finalContent.text) as CrafterData | null;
      if (parsed?.type === "crafter") return parsed;
    }
    const content = getBlockContent(props.block.blockId);
    if (content) {
      const parsed = safeParseBlockJson(content) as CrafterData | null;
      if (parsed?.type === "crafter") return parsed;
    }
    return null;
  });

  const markdownStreamer = createMemo(() => {
    const data = crafterData();
    if (!data?.content) return null;
    return createSolidStreamingMarkdown();
  });

  createEffect(() => {
    const streamer = markdownStreamer();
    const data = crafterData();
    if (streamer && data?.content) {
      streamer.reset();
      streamer.append(data.content);
    }
  });

  createEffect(
    on(
      () => crafterData()?.name,
      (name, prevName) => {
        if (!name) return;

        if (prevName !== undefined && prevName !== name) {
          setSavedScopes(new Set<string>());
        }

        let cancelled = false;
        const loadScopes = async (): Promise<void> => {
          const scopes = await rt.actions.listSkillScopes?.(name);
          if (cancelled || scopes === undefined) return;
          // Union with prior scopes so a stale list response cannot erase a scope we
          // just saved (eventual consistency / cache lag on GET /skills).
          setSavedScopes((prev) => new Set([...scopes, ...prev]));
        };
        void loadScopes();

        onCleanup(() => {
          cancelled = true;
        });
      }
    )
  );

  createEffect(() => {
    const data = crafterData();
    if (data?.category) setSelectedCategory(data.category);
  });

  const canSaveUser = () => !savedScopes().has("user");
  const canSaveProject = () => !savedScopes().has("project");
  const allSaved = () => !canSaveUser() && !canSaveProject();
  const savedAnyScope = () => savedScopes().size > 0;

  let popoverRef: HTMLDivElement | undefined;
  let buttonRef: HTMLButtonElement | undefined;

  const handleClickOutside = (e: MouseEvent) => {
    const target = e.target as Node;
    if (popoverRef && !popoverRef.contains(target) && buttonRef && !buttonRef.contains(target)) {
      setShowScopeMenu(false);
    }
  };

  onCleanup(() => {
    document.removeEventListener("mousedown", handleClickOutside);
  });

  const openScopeMenu = () => {
    if (saveState() === "saving" || allSaved()) return;
    if (saveState() === "error") setSaveState("idle");
    setShowScopeMenu(true);
    document.addEventListener("mousedown", handleClickOutside);
  };

  const closeScopeMenu = () => {
    setShowScopeMenu(false);
    document.removeEventListener("mousedown", handleClickOutside);
  };

  const handleSave = (scope: "user" | "project") => {
    saveSkill(scope).catch((error: unknown) => console.error("Failed to save skill", error));
  };

  const saveSkill = async (scope: "user" | "project") => {
    const data = crafterData();
    if (!data || saveState() === "saving") return;

    closeScopeMenu();
    setSaveState("saving");
    rt.actions.trackFeature?.({ feature: "chat.skill_save", via: "button" });
    const ok = await rt.actions.saveSkill?.({
      name: data.name,
      displayName: data.displayName,
      description: data.description,
      content: data.content,
      scope: scope,
      context: data.context,
      agent: data.agent,
      category: selectedCategory(),
    });

    if (ok) {
      setSavedScopes((prev) => new Set([...prev, scope]));
      setSaveState("idle");
    } else {
      setSaveState("error");
    }
  };

  const handleDownload = () => {
    const data = crafterData();
    if (!data) return;

    const lines: string[] = [
      "---",
      `name: ${data.name}`,
      `displayName: ${data.displayName}`,
      `description: ${data.description}`,
      `category: ${selectedCategory()}`,
    ];
    if (data.context) lines.push(`context: ${data.context}`);
    if (data.context === "agent" && data.agent) lines.push(`agent: ${data.agent}`);
    lines.push("---");
    lines.push("");
    lines.push(data.content);

    const markdown = lines.join("\n");
    const filename = `${sanitizeForFilename(data.name)}.md`;
    void rt.actions.saveGeneratedFile?.({ filename, mimeType: "text/markdown", text: markdown });
  };

  return (
    <Show when={crafterData()}>
      {(data) => (
        <div class="overflow-hidden rounded-lg border border-gray-200 dark:border-slate-700">
          <div class="flex items-center gap-3 bg-gray-50 px-3 py-2.5 dark:bg-slate-800/30">
            <div class="flex size-7 shrink-0 items-center justify-center rounded-lg bg-amber-100 dark:bg-amber-900/30">
              <TbWand class="size-4 text-amber-600 dark:text-amber-400" />
            </div>
            <div class="min-w-0 flex-1">
              <div class="flex items-center gap-2">
                <span class="truncate text-sm font-medium text-primary-text">{data().displayName}</span>
                <Show when={data().context === "agent"}>
                  <span class="flex shrink-0 items-center gap-1 rounded bg-cyan-100 px-1.5 py-0.5 text-xs text-cyan-700 dark:bg-cyan-900/30 dark:text-cyan-400">
                    <TbRobot class="size-3" />
                    {data().agent || "auto"}
                  </span>
                </Show>
              </div>
              <div class="mt-0.5 text-xs text-secondary-text">@{data().name}</div>
            </div>
          </div>

          <Show when={data().description}>
            <div class="border-t border-gray-100 px-3 py-2 dark:border-slate-700/50">
              <p class="text-xs text-secondary-text">{data().description}</p>
            </div>
          </Show>

          <Show when={data().content}>
            <div class="border-t border-gray-100 p-3 dark:border-slate-700/50">
              <ScrollableFadeContainer maxHeight="300px">
                <div class="max-w-full text-xs [&_h1]:mt-0 [&_h2]:mt-0 [&_h3]:mt-0 [&_li]:leading-relaxed [&_ol]:my-2 [&_ol]:space-y-1 [&_p]:mb-2 [&_p]:leading-relaxed [&_ul]:my-2 [&_ul]:space-y-1 [&>*:first-child]:mt-0 [&>*:last-child]:mb-0">
                  <Show when={markdownStreamer()}>
                    {(streamer) => <SolidStreamingMarkdown doc={streamer().doc} options={defaultMarkdownOptions} />}
                  </Show>
                </div>
              </ScrollableFadeContainer>
            </div>
          </Show>

          <div class="flex items-center justify-between border-t border-gray-100 px-3 py-2.5 dark:border-slate-700/50">
            <Show when={rt.actions.saveGeneratedFile} fallback={<span />}>
              <StTooltip content={rt.t("crafter_download")}>
                <StTooltipTrigger
                  as="button"
                  type="button"
                  onClick={handleDownload}
                  class="group/btn flex shrink-0 cursor-pointer items-center justify-center rounded-lg border border-muted-border bg-tile p-1.5 transition-all hover:bg-tile-hover"
                >
                  <TbDownload
                    size={16}
                    class="stroke-2 transition-transform group-hover/btn:scale-hover group-active/btn:scale-active"
                  />
                </StTooltipTrigger>
              </StTooltip>
            </Show>
            <Show when={rt.actions.saveSkill}>
              <div class="relative">
                <Show when={showScopeMenu()}>
                  <div
                    ref={popoverRef}
                    class="absolute right-0 bottom-full mb-1 w-64 overflow-hidden rounded-lg border border-gray-200 bg-white shadow-lg dark:border-slate-600 dark:bg-slate-800"
                  >
                    <div class="border-b border-gray-100 px-3 py-2 dark:border-slate-700">
                      <p class="mb-1.5 text-xs font-medium text-secondary-text">{rt.t("crafter_category_label")}</p>
                      <div class="flex gap-1.5">
                        <button
                          type="button"
                          onClick={() => setSelectedCategory("skill")}
                          class={`flex flex-1 items-center justify-center gap-1 rounded px-2 py-1.5 text-xs font-medium transition-colors ${
                            selectedCategory() === "skill"
                              ? "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400"
                              : "bg-gray-50 text-secondary-text hover:bg-gray-100 dark:bg-slate-700/50 dark:hover:bg-slate-700"
                          }`}
                        >
                          <TbWand class="size-3" />
                          {rt.t("crafter_category_skill")}
                        </button>
                        <button
                          type="button"
                          onClick={() => setSelectedCategory("persona")}
                          class={`flex flex-1 items-center justify-center gap-1 rounded px-2 py-1.5 text-xs font-medium transition-colors ${
                            selectedCategory() === "persona"
                              ? "bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400"
                              : "bg-gray-50 text-secondary-text hover:bg-gray-100 dark:bg-slate-700/50 dark:hover:bg-slate-700"
                          }`}
                        >
                          <TbUser class="size-3" />
                          {rt.t("crafter_category_persona")}
                        </button>
                      </div>
                    </div>
                    <div class="border-b border-gray-100 px-3 py-2 dark:border-slate-700">
                      <p class="text-xs font-medium text-secondary-text">{rt.t("crafter_save_as")}</p>
                    </div>
                    <Show when={canSaveUser()}>
                      <button
                        type="button"
                        onClick={() => handleSave("user")}
                        class="block w-full px-3 py-2.5 text-left hover:bg-gray-50 dark:hover:bg-slate-700/50"
                      >
                        <span class="text-sm font-medium text-primary-text">{rt.t("crafter_scope_user")}</span>
                        <p class="mt-0.5 text-xs text-secondary-text">{rt.t("crafter_scope_user_desc")}</p>
                      </button>
                    </Show>
                    <Show when={canSaveProject()}>
                      <button
                        type="button"
                        onClick={() => handleSave("project")}
                        class="block w-full px-3 py-2.5 text-left hover:bg-gray-50 dark:hover:bg-slate-700/50"
                      >
                        <span class="text-sm font-medium text-primary-text">{rt.t("crafter_scope_project")}</span>
                        <p class="mt-0.5 text-xs text-secondary-text">{rt.t("crafter_scope_project_desc")}</p>
                      </button>
                    </Show>
                  </div>
                </Show>

                <Show
                  when={!allSaved()}
                  fallback={
                    <div class="flex items-center gap-2">
                      <Show when={savedScopes().has("user")}>
                        <span class={`rounded px-1.5 py-0.5 text-xs ${getScopeBadgeClasses("user")}`}>
                          {rt.t("crafter_scope_user")}
                        </span>
                      </Show>
                      <Show when={savedScopes().has("project")}>
                        <span class={`rounded px-1.5 py-0.5 text-xs ${getScopeBadgeClasses("project")}`}>
                          {rt.t("crafter_scope_project")}
                        </span>
                      </Show>
                      <button
                        type="button"
                        disabled
                        class="flex items-center gap-1.5 rounded-lg bg-green-600 px-3 py-1.5 text-sm font-medium text-white"
                      >
                        <TbCheck class="size-4" />
                        {rt.t("crafter_saved")}
                      </button>
                    </div>
                  }
                >
                  <Show
                    when={saveState() !== "saving"}
                    fallback={
                      <button
                        type="button"
                        disabled
                        class="flex items-center gap-1.5 rounded-lg bg-amber-600 px-3 py-1.5 text-sm font-medium text-white opacity-75"
                      >
                        <TbLoader2 class="size-4 animate-spin" />
                        {rt.t("crafter_saving")}
                      </button>
                    }
                  >
                    <div class="flex flex-wrap items-center justify-end gap-2">
                      <Show when={savedScopes().has("user")}>
                        <span class={`rounded px-1.5 py-0.5 text-xs ${getScopeBadgeClasses("user")}`}>
                          {rt.t("crafter_scope_user")}
                        </span>
                      </Show>
                      <Show when={savedScopes().has("project")}>
                        <span class={`rounded px-1.5 py-0.5 text-xs ${getScopeBadgeClasses("project")}`}>
                          {rt.t("crafter_scope_project")}
                        </span>
                      </Show>
                      <Show when={savedAnyScope() && !allSaved()}>
                        <span class="inline-flex items-center gap-1 rounded-md border border-green-200/80 bg-green-100 px-2 py-1 text-xs font-medium text-green-800 dark:border-green-800/80 dark:bg-green-950/50 dark:text-green-300">
                          <TbCheck class="size-3.5 shrink-0" />
                          {rt.t("crafter_saved")}
                        </span>
                      </Show>
                      <button
                        ref={buttonRef}
                        type="button"
                        onClick={openScopeMenu}
                        class={`flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-medium ${
                          saveState() === "error"
                            ? "bg-red-50 text-red-600 hover:bg-red-100 dark:bg-red-900/20 dark:text-red-400 dark:hover:bg-red-900/30"
                            : savedAnyScope() && !allSaved()
                              ? "border border-amber-600/80 bg-amber-50/80 text-amber-800 hover:bg-amber-100 dark:border-amber-500/60 dark:bg-amber-950/30 dark:text-amber-200 dark:hover:bg-amber-950/50"
                              : "bg-amber-600 text-white hover:bg-amber-700"
                        }`}
                      >
                        <TbPlus class="size-4 shrink-0" />
                        <Show
                          when={saveState() === "error"}
                          fallback={savedAnyScope() && !allSaved() ? rt.t("crafter_save_as") : rt.t("crafter_save")}
                        >
                          {rt.t("crafter_save_error")}
                        </Show>
                      </button>
                    </div>
                  </Show>
                </Show>
              </div>
            </Show>
          </div>
        </div>
      )}
    </Show>
  );
}
